import CostAllocation from "./CostAllocation";
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container, Row, Col, Table, Button, Spinner, Alert, Badge } from "react-bootstrap";
import { authFetch } from "../utils/authFetch";

const getRecTypeLabel = (type) => {
  const t = Number(type);
  if (t === 0) return "INTRARE";
  if (t === 1) return "NIR";
  if (t === 2) return "PROCES VERBAL";
  if (t === 3) return "BON CONSUM";
  if (t === 4) return "BON TRANSFER";
  if (t === 5) return "IESIRE";
  if (t === 20) return "ÎNCASARE";
  if (t === 21) return "PLATĂ";
  return `Notă ${type}`;
};

function formatDateRo(val) {
  if (!val) return "";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

export default function NcView() {
  const { rec_type, doc_id } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await authFetch(`/nc/${rec_type}/${doc_id}`);
        setDoc(data);
      } catch (e) {
        setError(String(e.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [rec_type, doc_id]);

  const totals = useMemo(() => {
    if (!doc || !doc.linii) return { debit: 0, credit: 0, diff: 0 };
    const debit = doc.linii.reduce((s, r) => s + (Number(r.debit) || 0), 0);
    const credit = doc.linii.reduce((s, r) => s + (Number(r.credit) || 0), 0);
    return { debit, credit, diff: +(debit - credit).toFixed(2) };
  }, [doc]);

  if (loading) return <Container className="py-3"><Spinner animation="border" /> Se încarcă...</Container>;
  if (error) return <Container className="py-3"><Alert variant="danger">{error}</Alert></Container>;
  if (!doc || !doc.linii) return <Container className="py-3 text-danger">Nota nu a fost găsită.</Container>;

  const balanced = totals.diff === 0;

  return (
    <Container className="py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
            ← Înapoi
          </Button>
          <h3 className="mb-0 ms-2">
            Nota Contabilă #{doc_id}
          </h3>
          <Badge bg="info" className="ms-2">{getRecTypeLabel(rec_type)}</Badge>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={3}>
          <div className="text-muted small">Număr Document</div>
          <div className="fw-bold">{doc.doc_no || "-"}</div>
        </Col>
        <Col md={3}>
          <div className="text-muted small">Data NC</div>
          <div className="fw-bold">{formatDateRo(doc.data_nc) || "-"}</div>
        </Col>
        <Col md={6}>
          <div className="text-muted small">Explicație generală</div>
          <div className="fw-bold">{doc.explicatie || "-"}</div>
        </Col>
      </Row>

      <CostAllocation recType={rec_type} docId={doc_id} />
      <h5 className="mb-3">Linii Notă Contabilă</h5>
      <Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th>ID Linie</th>
            <th>Cont</th>
            <th>Explicație</th>
            <th className="text-end">Debit</th>
            <th className="text-end">Credit</th>
            <th>Cont Coresp.</th>
          </tr>
        </thead>
        <tbody>
          {doc.linii.map((ln) => (
            <tr key={ln.id}>
              <td>{ln.id}</td>
              <td className="fw-bold text-primary">{ln.account}</td>
              <td>{ln.bxexplic || ""}</td>
              <td className="text-end fw-bold">
                {Number(ln.debit || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2 })}
              </td>
              <td className="text-end fw-bold">
                {Number(ln.credit || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2 })}
              </td>
              <td className="text-muted">{ln.accountcoresp || ""}</td>
            </tr>
          ))}
          {doc.linii.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center text-muted py-4">Nu există linii în această notă.</td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr className="bg-light">
            <td colSpan={3} className="text-end fw-bold">Total:</td>
            <td className="text-end fw-bold text-primary">
              {totals.debit.toLocaleString("ro-RO", { minimumFractionDigits: 2 })}
            </td>
            <td className="text-end fw-bold text-success">
              {totals.credit.toLocaleString("ro-RO", { minimumFractionDigits: 2 })}
            </td>
            <td></td>
          </tr>
          <tr>
            <td colSpan={3} className="text-end fw-bold">Diferență (D - C):</td>
            <td colSpan={3} className={"fw-bold " + (balanced ? "text-success" : "text-danger")}>
              {totals.diff.toLocaleString("ro-RO", { minimumFractionDigits: 2 })}
            </td>
          </tr>
        </tfoot>
      </Table>
    </Container>
  );
}

